<script setup>
import { computed, onMounted } from 'vue';
import { useUi } from '@/composables/use-ui';
import { useBillingMerchant } from '@/composables/use-billing';
import { useAccountInvitations } from '@/composables/use-account-invitation';
import { useUnreadNotificationsCount } from '@/composables/use-notifs';
import MenuList from './list.vue';
import useGlobalSnack from '@/components/commons/global-snack/composables';

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  toggleSidebar: {
    type: Function,
    required: true,
  },
});

const { collapsed, toggleSidebar } = props;

const { show } = useGlobalSnack();
const { windowHeight, windowWidth, toolbarHeightPx, mobileBreakpointPx } = useUi();
const isMobile = computed(() => windowWidth.value < mobileBreakpointPx);
const menuHeight = computed(() => windowHeight.value - toolbarHeightPx);
const { 
  config: merchantConfig, 
  loadMerchantConfig,
  generateUrlToDashboard,
} = useBillingMerchant();

// autoload
loadMerchantConfig();

// Get unread notifications count
const { unreadCount, refresh: refreshUnreadCount } = useUnreadNotificationsCount();

const menu = computed(() => [
  {
    type: 'solo',
    name: 'Dashboard',
    linkTo: '/dashboard',
    icon: 'las la-icons',
  },
  {
    type: 'divider',
  },
  {
    type: 'group',
    group: 'FACILITY',
    menu: [
      {
        id: 'booking', // 'id' is used to identify the menu item
        name: 'Bookings',
        icon: 'las la-calendar-alt',
        linkTo: '/dashboard/bookings/today',
      },
      {
        name: 'Patients',
        icon: 'las la-user-injured',
        linkTo: '/dashboard/patients',
      },
      // {
      //   name: 'Active Encounters',
      //   icon: 'las la-book-medical',
      //   linkTo: '/dashboard/encounters/active',
      // },
      // {
      //   name: 'All Encounters',
      //   icon: 'las la-notes-medical',
      //   linkTo: '/dashboard/encounters/all',
      // },
      merchantConfig.value?.status === 'active' && {
        id: 'transactions',
        name: 'Transactions',
        icon: 'las la-file-invoice-dollar',
        iconAppend: 'las la-external-link-alt',
        handler: async () => {
          try {
            show(`Redirecting to billing dashboard`, { type: 'info' });
            const dashboard = await generateUrlToDashboard();
            window.open(dashboard.url, '_blank');
          } catch (error) {
            console.error(error);
            show(`Failed to load transactions. ${error.message}`, { type: 'error' });
          }
        },
      },
      // {
      //   name: 'Shared Health Records',
      //   icon: 'las la-notes-medical',
      //   linkTo: '/dashboard/shared',
      //   meta: {
      //     forPatient: true,
      //   },
      // },
      {
        name: 'Notifications',
        icon: 'las la-bell',
        linkTo: '/dashboard/notifs',
        badge: unreadCount.value > 0 ? unreadCount.value : null,
      },
    ].filter(Boolean),
  },
  // {
  //   type: 'divider',
  // },
  // {
  //   type: 'group',
  //   group: 'MODULES',
  //   menu: [
  //     {
  //       name: 'Billing',
  //       icon: 'las la-file-invoice-dollar',
  //       linkTo: '/modules/billing',
  //     }
  //   ]
  // },
]);

const bottomMenu = [
  {
    type: 'divider',
  },
  {
    type: 'solo',
    name: 'Settings',
    linkTo: '/settings/account/personal',
    icon: 'las la-cog',
  },
];

const {
  list: listInvitations,
  remainingInvites,
} = useAccountInvitations();

onMounted(() => {
  listInvitations();
});
</script>

<template>
  <div 
    class="flex flex-col justify-between z-50 transition-all duration-300 overflow-hidden"
    :class="collapsed ? 'p-2' : 'p-4'"
    :style="{ height: `${menuHeight}px`, 'margin-top': isMobile ? `${toolbarHeightPx}px` : '' }"
  >
    <!-- Main Navigation -->
    <div class="flex flex-col gap-2">
      <menu-list :menu="menu" :collapsed="collapsed" @close="$emit('close')" />
    </div>

    <!-- Bottom Navigation -->
    <div class="flex flex-col gap-2">
      <menu-list :menu="bottomMenu" :collapsed="collapsed" @close="$emit('close')" />
      
      <!-- Sidebar Toggle Button -->
      <div class="hidden lg:block mt-4">
        <button 
          @click="toggleSidebar"
          class="w-full flex items-center p-3 rounded-xl transition-all duration-200 group hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 text-gray-700 hover:text-gray-900 relative overflow-hidden"
          :class="collapsed ? 'justify-center' : 'gap-3'"
        >
          <!-- Icon Container -->
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-all duration-200 flex-shrink-0">
            <i 
              class="text-gray-600 group-hover:text-gray-700 transition-all duration-200 text-lg" 
              :class="collapsed ? 'las la-angle-right' : 'las la-angle-left'"
            ></i>
          </div>
          
          <!-- Text (completely hidden when collapsed) -->
          <span 
            v-show="!collapsed" 
            class="font-medium transition-all duration-200 whitespace-nowrap"
          >
            Collapse
          </span>
          
          <!-- Tooltip for collapsed state -->
          <div 
            v-if="collapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
          >
            Expand
          </div>
        </button>
      </div>
      
      <!-- Invite Card (when not collapsed) -->
      <div 
        v-if="!collapsed && false" 
        class="bg-gradient-to-br from-primary/5 to-red-50 border border-primary/20 rounded-2xl p-4 mt-4"
      >
        <div class="flex items-center gap-3 mb-3">
          <div class="w-8 h-8 bg-gradient-to-br from-primary to-red-600 rounded-lg flex items-center justify-center">
            <i class="las la-user-plus text-white text-lg"></i>
          </div>
          <h4 class="font-semibold text-gray-900 text-sm">Invite Users</h4>
        </div>
        <p class="text-xs text-gray-600 mb-3 leading-relaxed">
          You have {{ remainingInvites }} invites remaining
        </p>
        <button 
          class="btn btn-sm w-full bg-gradient-to-r from-primary to-red-600 text-white border-none hover:shadow-md transition-all duration-200"
          @click="$router.push('/dashboard/invite-users')"
        >
          <i class="las la-envelope text-lg mr-1"></i>
          Invite Now
        </button>
      </div>
    </div>
  </div>
</template>
